









import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import MapboxGL, { LngLatLike, LngLatBoundsLike } from 'mapbox-gl'
import {
  IEntitySearchResultOfCustomerLeadLightModel, ICustomerLeadLightModel,
  $leadMails, $newObj, CustomerLeadSearchQuery
} from '@immosparrow/cockpit-api-v2'
import { Coordinates } from './models'

@Component
export default class MapBox extends Vue {
  @Prop({ default: false, required: true }) readonly dataLoaded!: boolean
  @Prop({ default: false, required: true }) readonly fetching!: boolean
  @Prop({ default: () => ({ items: [] }), required: true }) readonly leads!: IEntitySearchResultOfCustomerLeadLightModel;

  map!: MapboxGL.Map;
  hoveredPoint: string | undefined = undefined
  leadsDuplicates: {
    lat: number;
    lng: number;
    leads: ICustomerLeadLightModel[];
  }[] = []

  popups: Coordinates[] = []

  get id () {
    return this.$route.query?.leadListId as string
  }

  get allLeads () {
    return this.leads?.items || []
  }

  mounted () {
    MapboxGL.accessToken = process.env.VUE_APP_MAPBOX

    this.map = new MapboxGL.Map({
      container: 'map',
      style: 'mapbox://styles/ascarix/cjoe973rf8rf82rmi5fcykj8w',
      center: [8.2275, 46.8182],
      zoom: 22,
      pitch: 0
    })

    this.map.on('load', () => {
      this.map.getCanvas().style.cursor = ''
      this.map.resize()
      if (this.leads.items?.length) this.loadMarkers()
    })

    this.map.on('mousemove', 'points', (e) => {
      this.map.getCanvas().style.cursor = 'pointer'
      if (e.features?.length) {
        if (this.hoveredPoint) {
          this.map.removeFeatureState({
            source: 'points',
            id: this.hoveredPoint
          })
        }
        this.hoveredPoint = e.features[0].id as string
        this.map.setFeatureState({
          source: 'points',
          id: this.hoveredPoint
        }, {
          hover: true
        })
      }
    })

    this.map.on('mouseleave', 'points', () => {
      if (this.hoveredPoint) {
        this.map.setFeatureState({
          source: 'points',
          id: this.hoveredPoint
        }, {
          hover: false
        })
      }
      this.hoveredPoint = undefined
      this.map.getCanvas().style.cursor = ''
    })
  }

  beforeDestroy () {
    this.map.remove()
  }

  async loadAllLeads () {
    if (!this.leads?.items?.length && this.id && !this.fetching) {
      const query = $newObj(CustomerLeadSearchQuery)
      query.maxItemCount = 100
      const newLeads = await $leadMails.findLeads(this.id, query) || { items: [] }
      this.$emit('update-leads', newLeads)
    }
  }

  async loadMarkers () {
    await this.loadAllLeads()

    const features: any[] = []

    this.allLeads.forEach((lead, index) => {
      if (lead.publication?.address?.coordinates !== undefined) {
        if (lead.publication?.address?.coordinates?.latitude && lead.publication?.address?.coordinates?.longitude) {
          const findDuplication = this.leadsDuplicates.findIndex(item => item.lat === lead.publication?.address?.coordinates?.latitude && item.lng === lead.publication.address.coordinates.longitude)
          if (findDuplication === -1) {
            this.leadsDuplicates.push({
              lng: lead.publication.address.coordinates.longitude,
              lat: lead.publication.address.coordinates.latitude,
              leads: [lead]
            })
          } else {
            this.leadsDuplicates[findDuplication].leads.push(lead)
          }
        }
        features.push({
          id: index,
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lead.publication.address.coordinates.longitude, lead.publication.address.coordinates.latitude] as LngLatLike
          },
          properties: {
            icon: {
              icon: 'monument'
            },
            leadId: lead.publication.id
          }
        })
      }
    })

    this.map.addSource('points', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features
      }
    })
    this.fitToBounds(features)

    const agencyColor = this.$store.getters.agencyTheme

    const colorExpressionPoint = [
      'case',
      ['==', ['feature-state', 'hover'], true], 0.5,
      1
    ]

    const up: any = {
      id: 'points',
      type: 'circle',
      source: 'points',
      paint: {
        'circle-color': agencyColor,
        'circle-radius': 6,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
        'circle-opacity': colorExpressionPoint
      }
    }

    this.map.addLayer(up)

    this.allLeads.forEach(lead => {
      if (lead.publication?.id) {
        const coord = lead.publication?.address?.coordinates
        if (coord?.longitude && coord?.latitude) {
          this.createPopup({ lat: coord.latitude, lng: coord.longitude }, lead)
        }
      }
    })
  }

  fitToBounds (points: any) {
    const coordsMin = [Math.min(...points.map((o: any) => {
      return o.geometry.coordinates[0]
    })), Math.min(...points.map((o: any) => {
      return o.geometry.coordinates[1]
    }))]
    const coordsMax = [Math.max(...points.map((o: any) => {
      return o.geometry.coordinates[0]
    })), Math.max(...points.map((o: any) => {
      return o.geometry.coordinates[1]
    }))]
    this.map.fitBounds([coordsMin, coordsMax] as LngLatBoundsLike, { duration: 0, padding: 50, maxZoom: 16 })
  }

  createPopup (latLng: Coordinates, lead: ICustomerLeadLightModel) {
    const popup = new MapboxGL.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom'
    })
    this.map.getCanvas().style.cursor = 'pointer'

    const price = lead.publication?.financialInfo?.totalPriceCalculated
    const currency = lead.publication?.financialInfo?.currency?.text || 'CHF'
    const currencySpan = price ? `<span class="currency">${currency}</span> ` : ''

    const div = document.createElement('div')
    const duplicateLeads = this.leadsDuplicates.find(item => item.lng === latLng.lng && item.lat === latLng.lat)
    if (duplicateLeads && duplicateLeads.leads?.length > 1) {
      div.innerHTML = '<div id="' + `${lead.publication?.id}` + '"class="lead-popup"><span class="price">' + `${duplicateLeads.leads.length} ads` + '</span></div>'
      div.addEventListener('click', () => {
        this.setMultipleSelectedLeads(duplicateLeads.leads)
      })
    } else {
      div.innerHTML = '<div id="' + `${lead.publication?.id}` + '"class="lead-popup"><span class="price">' + `${currencySpan}${price ? this.abbreviateNumber(price) : 'On Request'}` + '</span></div>'
      div.addEventListener('click', () => {
        this.setSelectedLead(lead)
      })
    }

    if (!this.popups.find(item => item.lat === latLng.lat && item.lng === latLng.lng)) {
      popup
        .setLngLat(latLng)
        .setDOMContent(div)
        .addTo(this.map)
      this.popups.push(latLng)
    }
  }

  abbreviateNumber (value: number) {
    let newValue = value
    const suffixes = ['', 'K', 'M', 'B', 'T']
    let suffixNum = 0
    while (newValue >= 1000) {
      newValue /= 1000
      suffixNum = suffixNum + 1
    }

    const stringNumber = newValue.toPrecision(3)
    return stringNumber + suffixes[suffixNum]
  }

  setSelectedLead (lead: ICustomerLeadLightModel) {
    this.$store.commit('setMultipleLeads', [])
    this.$store.commit('setLead', lead)
    this.$bvModal.show('modal-1')
    if (!lead.customerViewedTime) {
      lead.customerViewedTime = new Date()
      this.$store.dispatch('updateViewedStatus', lead.id)
    }
  }

  setMultipleSelectedLeads (leads: ICustomerLeadLightModel[]) {
    this.$store.commit('setMultipleLeadsIndex', 0)
    this.$store.commit('setLead', null)
    this.$store.commit('setMultipleLeads', leads)
    this.$bvModal.show('modal-1')
  }

  @Watch('fetching')
  onFetchingChanged (newValue: boolean, oldValue: boolean) {
    if (oldValue && !newValue && this.dataLoaded) {
      this.map.resize()
      this.loadMarkers()
    }
  }
}
